import { useTranslation } from 'react-i18next'

import { Message, MessageType } from 'lib/_types'
import {
  useReadAttachmentMutation,
  useReadVideoMutation,
} from 'store/api/yay/chat'

import ImageLoader from 'components/ImageLoader'

import { useMessageMediaInfo } from '../hooks'

type Props = {
  message: Message
  isCurrentUserMessage: boolean
  onPreviewMedia: (payload: { type: 'image' | 'video'; url: string }) => void
}

const ALLOWED_TYPE: Readonly<MessageType[]> = [
  'image',
  'eternal_image',
  'video',
  'eternal_video',
]

export const ChatMessageMedia = ({
  message,
  isCurrentUserMessage,
  onPreviewMedia,
}: Props) => {
  if (!ALLOWED_TYPE.includes(message?.message_type)) return null

  const { t } = useTranslation()
  const [readVideo, { isSuccess: isVideoRead }] = useReadVideoMutation()
  const [readAttachment, { isSuccess: isImageRead }] =
    useReadAttachmentMutation()

  const { mediaPath, thumbnailPath, isOneTime, isVideo, isDeleted } =
    useMessageMediaInfo({ message, isCurrentUserMessage })
  const isDeletedOrRead = isDeleted || isVideoRead || isImageRead

  const baseClass = isVideo
    ? 'Messages__item__view-video'
    : 'Messages__item__view-img'

  const ownClass = `${baseClass}-${
    isCurrentUserMessage ? 'owned' : 'not-owned'
  }`

  const onClick = async () => {
    if (isOneTime && !isCurrentUserMessage) {
      const res = await (isVideo ? readVideo : readAttachment)({
        chatId: message.room_id,
        ids: [message.id],
      })

      if (res?.['data']?.result != 'success') {
        return
      }
    }

    if (!isDeletedOrRead) {
      onPreviewMedia({ type: isVideo ? 'video' : 'image', url: mediaPath })
    }
  }

  if (isVideo && !message.video_processed) {
    return null
  }

  if (isDeletedOrRead) {
    return (
      <a className={`${ownClass} ${ownClass}--read`}>
        {t('chat.message.opened')}
      </a>
    )
  }

  if (!isCurrentUserMessage && isOneTime) {
    return (
      <a className={ownClass} onClick={onClick}>
        {t('common.tap_to_open')}
      </a>
    )
  }

  return (
    <a className={baseClass} onClick={onClick}>
      <ImageLoader url={thumbnailPath} preserveHeight="160px" />
    </a>
  )
}
